import { useQuery } from "@tanstack/react-query";

import { createCmmdApiClient } from "../environments/cmmdApi";

/**
 * Whether the signed-in user runs the CMMD platform.
 *
 * CMMD is the control plane and the only authority on this, answering from
 * active membership of the CMMD Platform organization. It is a different
 * question from `permissions.isAdmin` on the same payload, which reports the
 * caller's role in their active workspace and is true for anyone who owns a
 * workspace.
 *
 * This decides what the UI *offers*. The server enforces the same rule on the
 * endpoints themselves, so a stale or optimistic answer here cannot expose
 * anything — it can only offer a target that then refuses.
 */
const CMMD_PLATFORM_ADMIN_QUERY_KEY = ["cmmd", "platform-admin"] as const;

export function useCmmdPlatformAdmin(enabled: boolean): boolean {
  const query = useQuery({
    queryKey: CMMD_PLATFORM_ADMIN_QUERY_KEY,
    queryFn: () => createCmmdApiClient().getBootstrap(),
    enabled,
    staleTime: 5 * 60_000,
  });

  // Every non-answer — not yet loaded, disabled, CMMD unreachable, a
  // deployment that does not send the field — is a negative one.
  return query.data?.permissions.isPlatformAdmin === true;
}
